import { Drawer, Box, Button } from '@mui/material'
import type { FC } from 'react'
import React, { useContext, useState } from 'react'

import { getIncomeLabel } from '@/components/IncomeCard/IncomeTypeLabel'
import ctx from '@/context/AppContext'
import { useUnableToVerifyIncomeMutation } from '@/lib/apollo'
import vouchLogger from '@/lib/vouchLogger'

import type { UnableToVerifyReason } from './constants'
import DrawerHeader from './DrawerHeader'
import DrawerTitle from './DrawerTitle'
import PreviewStep from './PreviewStep'
import { ReasonStep } from './ReasonStep'
import SubmitStep from './SubmitStep'
import TrustedSourcesFooter from './TrustedSourcesFooter'

interface Props {
  customerId: string
  applicationId: string
  subjectId: string
  incomeId: string
  incomeType: string
  incomeStatus: string
  companyName: string
  mandatedTrustedSourcesEnabled?: boolean
  showTrustedSourcesRequiredLabel?: boolean
  onClose: () => void
  onSuccess: () => void
}

type FormStep = 'reason' | 'preview' | 'submit'

const UnableToVerifySidebar: FC<Props> = ({
  customerId,
  applicationId,
  subjectId,
  incomeId,
  incomeType,
  incomeStatus,
  companyName,
  mandatedTrustedSourcesEnabled,
  showTrustedSourcesRequiredLabel,
  onClose,
  onSuccess,
}) => {
  const [formStep, setFormStep] = useState<FormStep>(
    mandatedTrustedSourcesEnabled ? 'reason' : 'preview',
  )
  const [selectedReason, setSelectedReason] = useState<UnableToVerifyReason | undefined>(
    undefined,
  )

  const [unableToVerifyIncome, { data, loading, error }] =
    useUnableToVerifyIncomeMutation()

  const { personId } = useContext(ctx)
  const userId = personId ?? ''

  const handleSubmit = () => {
    setFormStep('submit')

    unableToVerifyIncome({
      variables: {
        customerId,
        applicationId,
        subjectId,
        incomeId,
        userId,
        reason: selectedReason,
      },
    })
      .then(onSuccess)
      .catch((err: Error) => {
        vouchLogger.error('Unable to verify income failed', {
          error: err,
          subjectId,
          customerId,
        })
      })
  }

  const handleRetry = () => {
    setFormStep('preview')
  }

  return (
    <Drawer
      anchor="right"
      open
      onClose={onClose}
      PaperProps={{
        sx: {
          width: '40%',
          borderLeft: '2px solid #f2f2f2',
          boxShadow: 'none',
        },
      }}
    >
      <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <DrawerHeader
          onClose={onClose}
          onBack={() => setFormStep('reason')}
          showBack={mandatedTrustedSourcesEnabled && formStep === 'preview'}
        />

        <DrawerTitle
          title="Unable to Verify Income"
          subtitle={`${getIncomeLabel(incomeType, incomeStatus)} - ${companyName}`}
        />

        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {formStep === 'reason' && (
            <ReasonStep
              selectedReason={selectedReason}
              onReasonChange={setSelectedReason}
            />
          )}
          {formStep === 'preview' && (
            <PreviewStep
              customerId={customerId}
              applicationId={applicationId}
              subjectId={subjectId}
              incomeId={incomeId}
              userId={userId}
              reason={selectedReason}
              incomeType={incomeType}
              incomeStatus={incomeStatus}
              companyName={companyName}
              mandatedTrustedSourcesEnabled={mandatedTrustedSourcesEnabled}
            />
          )}
          {formStep === 'submit' && (
            <SubmitStep
              loading={loading}
              success={!!data}
              error={error}
              onRetry={handleRetry}
            />
          )}
        </Box>

        {mandatedTrustedSourcesEnabled && formStep === 'reason' && (
          <TrustedSourcesFooter
            buttonLabel="Next step"
            disabled={!selectedReason}
            onClick={() => setFormStep('preview')}
            showTrustedSourcesRequiredLabel={showTrustedSourcesRequiredLabel}
          />
        )}

        {mandatedTrustedSourcesEnabled && formStep === 'preview' && (
          <TrustedSourcesFooter
            buttonLabel="Continue to confirm"
            onClick={handleSubmit}
            showTrustedSourcesRequiredLabel={showTrustedSourcesRequiredLabel}
          />
        )}

        {!mandatedTrustedSourcesEnabled && formStep === 'preview' && (
          <Box sx={{ pt: 2 }}>
            <Button
              variant="contained"
              fullWidth
              sx={{
                bgcolor: '#1B4554',
                '&:hover': { bgcolor: '#13323d' },
                textTransform: 'none',
                fontWeight: 600,
                py: 2,
              }}
              onClick={handleSubmit}
            >
              Confirm and verify
            </Button>
          </Box>
        )}
      </Box>
    </Drawer>
  )
}

export default UnableToVerifySidebar
